import { HTMLAttributes, ReactElement, useCallback, useMemo, useState } from "react";
import { RelativeRoutingType, useLocation, useParams } from "react-router-dom";
import { useCopyToClipboard } from "react-use";
import { Folder, Lock } from "@mui/icons-material";
import { Box, MenuItem, menuItemClasses, Stack, Tooltip } from "@mui/material";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPrefixes, { DatasetFileConfig } from "datasets/hooks/useDatasetPrefixes";
import DirectoryHeader from "files/components/DirectoryHeader";
import FileNotFound from "files/components/FileNotFound";
import FilePreview from "files/components/FilePreview";
import FileRow from "files/components/FileRow";
import FileTitle from "files/components/FileTitle";
import useBasePath from "files/hooks/useBasePath";
import useFileAccess from "files/hooks/useFileAccess";
import useFiles, { encodeObjectKey, resolveRouterPath } from "files/hooks/useFiles";
import FileInfo from "files/types/interfaces/fileInfo";
import ActionsMenu from "~/components/ActionsMenu";
import ListCard, { LinkListItem } from "~/components/ListCard";
import TextOverflow from "~/components/TextOverflow";
import useErrorToast from "~/contexts/error-toast";
import useUserPermission from "~/hooks/useUserPermission";
import { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const PrefixRow = ({ id, fileKey }: { id: string; fileKey: string }) => {
  const canRead = useFileAccess(fileKey, "read");
  const { data: dataset, mutate: mutateDataset } = useDataset();
  const { mutate: mutatePrefixes } = useDatasetPrefixes();
  const { datasetId } = useParams();
  const { showError } = useErrorToast();
  const [, copy] = useCopyToClipboard();
  const basePath = useBasePath();
  const [showActions, setShowActions] = useState(false);
  const canEdit = !!dataset?.actions_permitted.includes("edit:catalog-metadata");
  const routerPath = `${id}/${encodeObjectKey(resolveRouterPath(fileKey))}`;
  const subtitle = resolveRouterPath(fileKey.replace(/\/[^\/]+$/, ""));

  const handleCopyLink = useCallback(() => {
    setShowActions(false);
    trackEvent("dataset_file_config_link_copied", {
      dataset_id: datasetId,
      folder_prefix: fileKey,
    });
    const url = new URL(`${basePath}/${routerPath}`, window.location.origin);
    copy(url.toString());
  }, [basePath, copy, datasetId, fileKey, routerPath]);

  const handleRemove = useCallback(async () => {
    setShowActions(false);
    const resp = await fetch(`${VITE_CATALOG_API}/datasets/${datasetId}/file-configs/${id}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    });

    if (!resp.ok) {
      showError("Unable to remove folder from dataset");
    } else {
      trackEvent("dataset_file_config_removed", {
        dataset_id: datasetId,
        folder_prefix: fileKey,
      });
      mutatePrefixes();
      mutateDataset();
    }
  }, [datasetId, fileKey, id, mutateDataset, mutatePrefixes, showError]);

  const menuItems: ReactElement<HTMLAttributes<HTMLInputElement>>[] = useMemo(() => {
    const actions = [
      <MenuItem key="copy-link" onClick={handleCopyLink}>
        Copy Link
      </MenuItem>,
    ];

    if (canEdit) {
      actions.push(
        <MenuItem
          key="remove"
          sx={{
            color: "attention.main",
            [`&.${menuItemClasses.root}:hover`]: {
              color: "common.white",
              bgcolor: "attention.main",
            },
          }}
          onClick={handleRemove}
        >
          Remove
        </MenuItem>
      );
    }

    return actions;
  }, [canEdit, handleCopyLink, handleRemove]);

  return (
    <>
      <LinkListItem
        testId="dataset-file-config"
        disabled={!canRead}
        to={routerPath}
        navOptions={{
          relative: (fileKey.startsWith("..") ? "path" : "route") as RelativeRoutingType,
        }}
        icon={<Folder fontSize="small" sx={{ color: "#a98ff1" }} />}
      >
        <Box
          onMouseEnter={() => setShowActions(true)}
          onMouseLeave={() => setShowActions(false)}
          display="flex"
          alignItems="center"
          gap={4}
          height={subtitle ? 36 : 22}
          fontSize={14}
          width="100%"
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            <TextOverflow
              autoTooltip
              variant="subtitle1"
              sx={{ fontSize: 14, fontWeight: 400, opacity: canRead ? 1 : 0.7 }}
            >
              <FileTitle fileKey={fileKey} />
            </TextOverflow>
            {subtitle && (
              <TextOverflow
                autoTooltip
                variant="subtitle2"
                color="grey.600"
                fontSize={12}
                fontWeight={400}
              >
                {subtitle}
              </TextOverflow>
            )}
          </Box>
          <Box>
            {canRead ? (
              <ActionsMenu visible={showActions} menuItems={menuItems} placement="bottom-start" />
            ) : (
              <Tooltip title="You do not have permission to view to this folder">
                <Lock sx={{ fontSize: 16, mx: 1, opacity: 0.7 }} />
              </Tooltip>
            )}
          </Box>
        </Box>
      </LinkListItem>
    </>
  );
};

interface FileDirectoryProps {
  fileConfigs: DatasetFileConfig[];
  configsLoading: boolean;
}

const FileDirectory = ({ fileConfigs, configsLoading }: FileDirectoryProps) => {
  const canPreviewFile = useUserPermission("create:file-browser-download");
  const { state } = useLocation();
  const { "*": filePath = "" } = useParams();
  const preview = state?.preview as Pick<FileInfo, "key" | "size"> | undefined;
  const { data: prefixFiles, isLoading: filesLoading } = useFiles();

  const rows = useMemo(() => {
    if (filePath) {
      const prefixRoutes = fileConfigs.map(({ prefix }) => resolveRouterPath(prefix));
      // If we're at the top of a dataset folder, make the backlink go to root of dataset files
      const backLink = prefixRoutes.includes(filePath)
        ? `../${filePath.replace(/[^/]+/g, "..")}`
        : "..";

      return [
        <FileRow key="back" hideActions fileKey={backLink} />,
        ...(prefixFiles?.children ?? []).map((node) => {
          return (
            <FileRow
              key={node.key}
              fileKey={node.key}
              type={node.type}
              size={node.size}
              pendingAction={node.pending_action}
              updatedAt={node.updated_at}
              s3Uri={node.s3_uri}
              disabled={node.type === "file" && !canPreviewFile}
            />
          );
        }),
      ];
    }

    return fileConfigs.map(({ id, prefix }) => <PrefixRow key={id} id={id} fileKey={prefix} />);
  }, [filePath, fileConfigs, prefixFiles?.children, canPreviewFile]);

  if (fileConfigs && !filesLoading && prefixFiles && !prefixFiles.current_file) {
    return (
      <Box mt={2}>
        <FileNotFound />
      </Box>
    );
  }

  return (
    <Stack gap={4} data-testid="dataset-file-directory">
      <ListCard
        header={<DirectoryHeader />}
        loading={configsLoading || filesLoading}
        placeholder={{ height: 34 }}
      >
        {rows}
      </ListCard>
      {preview && <FilePreview file={preview} />}
    </Stack>
  );
};

export default FileDirectory;
